/* Reusable UI primitives: Button, Field, Modal, Toast, Skeleton, EmptyState, Badge, etc.
   Ported from the prototype to ES modules. */
import { useState, useEffect } from "react";
import Icon from "./Icon.jsx";

export function Button({ variant = "primary", size = "md", icon, iconRight, loading, disabled, className = "", children, ...rest }) {
  const cls = [
    "btn",
    `btn--${variant}`,
    size === "sm" && "btn--sm",
    size === "lg" && "btn--lg",
    !children && "btn--icon",
    className,
  ].filter(Boolean).join(" ");
  return (
    <button className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Spinner size={size === "sm" ? 14 : size === "lg" ? 20 : 16} /> : icon}
      {children}
      {iconRight}
    </button>
  );
}

export function Spinner({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: "spin 700ms linear infinite" }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Field({ label, helper, error, required, htmlFor, children, className = "" }) {
  return (
    <div className={`field ${className}`}>
      {label && (
        <label className="field__label" htmlFor={htmlFor}>
          {label}{required && <span className="req">*</span>}
        </label>
      )}
      {children}
      {error
        ? <div className="field__error" role="alert"><Icon.AlertCircle size={14} />{error}</div>
        : helper && <div className="field__helper">{helper}</div>}
    </div>
  );
}

export function Input({ size = "md", error, className = "", ...rest }) {
  return <input className={`input ${size === "lg" ? "input--lg" : ""} ${error ? "error" : ""} ${className}`} {...rest} />;
}
export function Textarea({ size = "md", error, className = "", ...rest }) {
  return <textarea className={`textarea ${size === "lg" ? "textarea--lg" : ""} ${error ? "error" : ""} ${className}`} {...rest} />;
}
export function Select({ size = "md", error, className = "", children, ...rest }) {
  return <select className={`select ${size === "lg" ? "select--lg" : ""} ${error ? "error" : ""} ${className}`} {...rest}>{children}</select>;
}

export function Badge({ variant = "neutral", children, noDot, style }) {
  return <span className={`badge badge--${variant} ${noDot ? "badge--no-dot" : ""}`} style={style}>{children}</span>;
}

export function Card({ variant = "", clickable, className = "", children, ...rest }) {
  return <div className={`card ${variant ? "card--" + variant : ""} ${clickable ? "card--clickable" : ""} ${className}`} {...rest}>{children}</div>;
}

export function Modal({ open, onClose, title, subtitle, size = "md", children, footer }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`modal ${size === "lg" ? "modal--lg" : size === "xl" ? "modal--xl" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title">
            {typeof title === "string" ? <h2 className="t-2xl" style={{ margin: 0 }}>{title}</h2> : title}
            {subtitle && <div className="modal__subtitle">{subtitle}</div>}
          </div>
          <button className="modal__close" onClick={onClose} aria-label="Close"><Icon.X size={18} /></button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

/* Toast — module-level singleton store. */
let toastIdCounter = 0;
const toastListeners = new Set();
let toastQueue = [];

export function showToast(payload) {
  const id = ++toastIdCounter;
  const t = { id, ...payload };
  toastQueue = [...toastQueue, t].slice(-3);
  toastListeners.forEach(l => l([...toastQueue]));
  const duration = payload.intent === "danger" ? null : payload.duration ?? (payload.intent === "warning" ? 8000 : 5000);
  if (duration) setTimeout(() => dismissToast(id), duration);
}
export function dismissToast(id) {
  toastQueue = toastQueue.filter(t => t.id !== id);
  toastListeners.forEach(l => l([...toastQueue]));
}

export function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    toastListeners.add(setItems);
    return () => toastListeners.delete(setItems);
  }, []);
  if (!items.length) return null;
  return (
    <div className="toast-host" aria-live="polite" aria-atomic="false">
      {items.map(t => (
        <div key={t.id} className={`toast toast--${t.intent || "info"}`}>
          <div style={{ marginTop: 1, color: "var(--" + (t.intent === "success" ? "success" : t.intent === "danger" ? "danger" : t.intent === "warning" ? "warning" : "primary") + ")" }}>
            {t.intent === "success" ? <Icon.Check size={16} /> :
             t.intent === "danger" ? <Icon.AlertCircle size={16} /> :
             t.intent === "warning" ? <Icon.AlertTriangle size={16} /> :
             <Icon.Info size={16} />}
          </div>
          <div style={{ flex: 1 }}>
            <div className="toast__title">{t.title}</div>
            {t.body && <div className="toast__body">{t.body}</div>}
          </div>
          <button className="toast__close" onClick={() => dismissToast(t.id)} aria-label="Dismiss"><Icon.X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

export function Skeleton({ w = "100%", h = 16, r, style }) {
  return <div className="skeleton" style={{ width: w, height: h, borderRadius: r ?? "var(--radius)", ...style }} />;
}

export function EmptyState({ icon, title, body, action }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon || <Icon.Sparkles size={40} />}</div>
      <div className="empty__title">{title}</div>
      {body && <div className="empty__body">{body}</div>}
      {action}
    </div>
  );
}

/* Image placeholder — varies tint by label hash for visual variety. */
export function Placeholder({ label, illustration, seed }) {
  const hash = (seed || label || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const variants = [
    { from: "#FCE4EC", to: "#FBC5D6", icon: "var(--primary)" },
    { from: "#FFF8FA", to: "#F2D9E1", icon: "var(--accent-pink)" },
    { from: "#FCE9B0", to: "#F4B400", icon: "var(--primary)" },
    { from: "#FBC5D6", to: "#F4B400", icon: "var(--primary)" },
    { from: "#F2D9E1", to: "#FCE4EC", icon: "var(--accent-pink)" },
  ];
  const v = variants[hash % variants.length];
  const bg = illustration
    ? "linear-gradient(135deg, var(--accent-pink-soft) 0%, #FCE4EC 60%, var(--accent-yellow-soft) 100%)"
    : `linear-gradient(135deg, ${v.from} 0%, ${v.to} 100%)`;
  return (
    <div className={`img-placeholder ${illustration ? "img-placeholder--illustration" : ""}`} style={{ background: bg }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", color: v.icon }}>
        {illustration ? <Icon.Bolt size={32} /> : <Icon.Image size={24} />}
        {label && <div className="img-placeholder__label" style={{ color: v.icon, opacity: 0.7 }}>{label}</div>}
      </div>
    </div>
  );
}

export function SmartImage({ src, alt = "", className = "", style, label, seed }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return <Placeholder label={label || alt} seed={seed || label || alt} />;
  }
  return <img src={src} alt={alt} className={className} style={style} onError={() => setErrored(true)} />;
}

export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", destructive, onConfirm, onClose }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{cancelLabel}</Button>
        <Button variant={destructive ? "destructive" : "primary"} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Button>
      </>}
    >
      <p className="t-base muted" style={{ margin: 0 }}>{body}</p>
    </Modal>
  );
}

export function BoltDeco({ size = 320, color = "currentColor", style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill={color} style={style} aria-hidden="true">
      <polygon points="55,5 25,55 45,55 40,95 75,40 55,40" />
    </svg>
  );
}

export function BrandMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="0" y="0" width="32" height="32" rx="8" fill="var(--accent-pink)" />
      <polygon points="18,5 9,18 15,18 13,27 23,13 17,13" fill="var(--accent-yellow)" />
    </svg>
  );
}
